"use client";

/**
 * fund-practical-info.tsx
 *
 * 3×2 grid of practical investment facts: min SIP, min lump sum, exit load,
 * lock-in, redemption settlement (T+N), fund age. Plus a "How to invest"
 * footnote and an optional SID (Scheme Information Document) link.
 *
 * Used in: src/app/funds/[slug]/page.tsx (bottom of report — never paywalled)
 */

import { ExternalLink } from "lucide-react";
import { formatRupees, stripHtml } from "@/lib/utils/format";

export interface FundPracticalInfoProps {
  minSip: number | null;
  minLumpsum: number | null;
  exitLoad: string | null;
  lockInPeriod: number | null; // years; 0 = no lock-in, 3 = ELSS
  fundAgeText: string | null;
  fundType: string;
  sidUrl: string | null;
}

/** Standard redemption T+N mapping by fund type */
function redemptionFor(fundType: string): string {
  const t = fundType.toLowerCase();
  if (t.includes("liquid") || t.includes("overnight")) return "T+1";
  if (t.includes("money market") || t.includes("ultra short")) return "T+1";
  if (t.includes("debt") || t.includes("bond") || t.includes("gilt") || t.includes("duration"))
    return "T+2";
  return "T+3"; // Equity, hybrid, ELSS, index
}

function lockInText(years: number | null): string {
  if (years === null || years === undefined || years <= 0) return "None";
  return `${years} year${years === 1 ? "" : "s"}`;
}

interface Tile {
  label: string;
  value: string;
  subtitle?: string;
  highlight?: boolean;
  /** Use a smaller, wrapping font — for long strings like exit load */
  small?: boolean;
}

export function FundPracticalInfo({
  minSip,
  minLumpsum,
  exitLoad,
  lockInPeriod,
  fundAgeText,
  fundType,
  sidUrl,
}: FundPracticalInfoProps) {
  const stripped = stripHtml(exitLoad);
  const cleanedExitLoad = stripped.length > 0 ? stripped : null;
  const tiles: Tile[] = [
    {
      label: "Min SIP",
      value: minSip !== null && minSip !== undefined ? formatRupees(minSip) : "-",
      subtitle: "per month",
    },
    {
      label: "Min lump sum",
      value:
        minLumpsum !== null && minLumpsum !== undefined ? formatRupees(minLumpsum) : "-",
      subtitle: "one-time",
    },
    {
      label: "Exit load",
      value: cleanedExitLoad ?? "Nil",
      small: !!cleanedExitLoad && cleanedExitLoad.length > 12,
    },
    {
      label: "Lock-in",
      value: lockInText(lockInPeriod),
      highlight: (lockInPeriod ?? 0) > 0,
    },
    {
      label: "Redemption",
      value: redemptionFor(fundType),
      subtitle: "settlement",
    },
    {
      label: "Fund age",
      value: fundAgeText && fundAgeText.trim().length > 0 ? fundAgeText : "-",
    },
  ];

  return (
    <section className="mt-6 space-y-4">
      <h2 className="text-foreground text-base font-serif">Practical info</h2>

      {/* 2x3 on mobile → 3x2 on md+ */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {tiles.map((tile) => (
          <div
            key={tile.label}
            className="bg-surface rounded-lg p-4 border border-surface-light/10"
          >
            <div className="text-muted-foreground text-[11px] uppercase tracking-wide mb-1.5">
              {tile.label}
            </div>
            <div
              className={`font-bold font-tabular ${
                tile.small
                  ? "text-[12px] leading-snug break-words"
                  : "text-[18px] leading-none"
              } ${tile.highlight ? "text-gold" : "text-foreground"}`}
            >
              {tile.value}
            </div>
            {tile.subtitle && (
              <div className="text-dim text-[11px] mt-1">{tile.subtitle}</div>
            )}
          </div>
        ))}
      </div>

      {/* How to invest footnote */}
      <div className="bg-surface/60 rounded-lg p-4 border border-surface-light/10">
        <div className="text-foreground text-xs font-bold mb-1">How to invest</div>
        <div className="text-muted-foreground text-xs leading-relaxed">
          Invest directly via the AMC website (zero commission) or through
          regulated platforms like Zerodha Coin, Groww, Kuvera, or MF Central.
          Always choose the <span className="text-foreground font-semibold">Direct plan</span>:{" "}
          it has a lower expense ratio than the Regular plan.
        </div>
      </div>

      {/* Optional SID link */}
      {sidUrl && (
        <a
          href={sidUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-gold text-xs font-semibold hover:underline"
        >
          View Scheme Information Document (SID)
          <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </section>
  );
}
